import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Button,
  Space,
  DatePicker,
  Select,
  Modal,
  InputNumber,
  Form,
  Tag,
  Input,
} from "antd";
import { S, T } from "./styles";
import {
  DownOutlined,
  RightOutlined,
  LeftOutlined,
  RightOutlined as NextIcon,
  CalendarOutlined,
  HomeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import TitleHead from "../Customs/TitleHead";
import { GeneralService } from "../../Services/GeneralService";
import { notify } from "../Customs/NotifyProvider";
import { formatDate } from "../Customs/dateFormatter";


const priceRows = [
  { key: "price_per_day", label: "Room Price", short: "ROOM" },
  { key: "online_price", label: "Online Price", short: "ONLINE" },
  { key: "price_per_hour", label: "Per Hour", short: "HOUR" },
  // { key: "full_book", label: "24 Hr Rate", short: "24HR" },
];

const dateKey = (date) => date.format("YYYY-MM-DD");
const calculatePercentPrice = (basePrice, percent) => {
  const base = Number(basePrice || 0);
  const hike = Number(percent || 0);
  return Math.round(base + (base * hike) / 100);
};

const fmtPrice = (n) =>
  "₹ " + Number(n).toLocaleString("en-IN");

/* ─── Component ──────────────────────────────────────────── */
const RoomByList = () => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [daysCount, setDaysCount] = useState(30);
  const [expanded, setExpanded] = useState({ 1: true, 2: true, 3: true, 4: true });
  const [prices, setPrices] = useState({});
  const [drag, setDrag] = useState(null);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [selectedRoomRate, setSelectedRoomRate] = useState(null);
  const [rateForm] = Form.useForm();
  const [overidePrice, setOveridePrice] = useState([]);

  const dates = useMemo(() =>
    Array.from({ length: daysCount }, (_, i) =>
      currentDate.startOf("month").add(i, "day")),
    [currentDate, daysCount]);

  const getRoombyType = async () => {
    try {
      const req = await GeneralService.getRoombyType()
      setData(req.data);
      // console.log(req.data);

    } catch (error) {
      console.error(error);

    }
  }

  const getOveridePrice = async () => {
    try {
      const req = await GeneralService.get("room_price_overrides")
      setOveridePrice(req.data);
      // console.log("OveridePrice",req.data);

    } catch (error) {
      console.error(error);

    }
  }

  useEffect(() => {
    if (!overidePrice?.length || !data?.length) return;

    const mappedPrices = {};

    overidePrice.forEach((item) => {
      const from = dayjs(item.from_date);
      const to = dayjs(item.to_date);

      for (
        let d = from.clone();
        d.isBefore(to.add(1, "day"));
        d = d.add(1, "day")
      ) {
        if (item.level === "type") {
          const group = data.find(
            (g) => g.room_type_id === item.room_type_id
          );

          if (!group) continue;

          const basePrice = Number(
            group.rooms?.[0]?.[item.price_key] || 0
          );

          const key = `type-${item.room_type_id}-${item.price_key}-${d.format(
            "YYYY-MM-DD"
          )}`;

          mappedPrices[key] = {
            percent: Number(item.percent),
            basePrice,
            finalPrice: calculatePercentPrice(
              basePrice,
              Number(item.percent)
            ),
            isEdited: true,
            fromType: true,
            overrideId: item.id,
          };
        }

        if (item.level === "room") {
          let room = null;

          data.forEach((group) => {
            const found = group.rooms?.find(
              (r) => r.id === item.room_id
            );

            if (found) room = found;
          });

          if (!room) continue;

          const basePrice = Number(
            room[item.price_key] || 0
          );

          const key = `room-${item.room_id}-${item.price_key}-${d.format(
            "YYYY-MM-DD"
          )}`;

          mappedPrices[key] = {
            percent: Number(item.percent),
            basePrice,
            finalPrice: calculatePercentPrice(
              basePrice,
              Number(item.percent)
            ),
            isEdited: true,
            fromType: false,
            overrideId: item.id,
          };
        }
      }
    });

    // console.log("DEFAULT PRICE STATE =>", mappedPrices);

    setPrices(mappedPrices);
  }, [overidePrice, data]);

  useEffect(() => {
    getRoombyType();
    getOveridePrice();
  }, [])

  const monthTitle = `${dates[0].format("D MMM YYYY")} – ${dates[dates.length - 1].format("D MMM YYYY")}`;

  /* ── Price helpers ── */
  const getPriceKey = ({ level, roomTypeId, id, priceKey, date }) =>
    level === "type"
      ? `type-${roomTypeId}-${priceKey}-${dateKey(date)}`
      : `room-${id}-${priceKey}-${dateKey(date)}`;

  const getRoomBasePrice = ({ room, priceKey }) => Number(room?.[priceKey] || 0);
  const getTypeBasePrice = ({ group, priceKey }) => Number(group.rooms?.[0]?.[priceKey] || 0);

  const getTypePriceData = ({ group, priceKey, date }) => {
    const key = getPriceKey({ level: "type", roomTypeId: group.room_type_id, priceKey, date });
    if (prices[key]) return prices[key];
    const basePrice = getTypeBasePrice({ group, priceKey });
    return { basePrice, finalPrice: basePrice, percent: 0, isEdited: false };
  };

  const getRoomPriceData = ({ group, room, priceKey, date }) => {
    const roomKey = getPriceKey({ level: "room", id: room.id, priceKey, date });
    const typeKey = getPriceKey({ level: "type", roomTypeId: group.room_type_id, priceKey, date });
    if (prices[roomKey]) return prices[roomKey];
    if (prices[typeKey]) {
      const typePercent = prices[typeKey].percent;
      const basePrice = getRoomBasePrice({ room, priceKey });
      return { basePrice, percent: typePercent, finalPrice: calculatePercentPrice(basePrice, typePercent), fromType: true, isEdited: true };
    }
    const basePrice = getRoomBasePrice({ room, priceKey });
    return { basePrice, finalPrice: basePrice, percent: 0, isEdited: false };
  };

  /* ── Drag ── */
  const startDrag = (payload) => setDrag({ ...payload, startIndex: payload.dateIndex, endIndex: payload.dateIndex });
  const moveDrag = (dateIndex) => { if (!drag) return; setDrag((p) => ({ ...p, endIndex: dateIndex })); };

  const openModal = (range, existingPercent = 0) => {
    setSelected(range);
    setModalOpen(true);
    setTimeout(() => form.setFieldsValue({ percent: existingPercent }), 0);
  };

  const openRoomRateModal = (group, room) => {
    const roomDetails = {
      room_type_id: group.room_type_id,
      room_type: group.room_type,
      room_id: room.id,
      room_name: room.room_name,
      room_number: room.room_number,
      current_values: {
        price_per_day: Number(room.price_per_day || 0),
        online_price: Number(room.online_price || 0),
        price_per_hour: Number(room.price_per_hour || 0),
        full_book: Number(room.full_book || 0),
      },
    };

    console.log("EDIT RATE CLICK ROOM DETAILS =>", roomDetails);

    setSelectedRoomRate({ group, room });
    rateForm.setFieldsValue(roomDetails.current_values);
    setRateModalOpen(true);
  };

  const updateRoomRates = async () => {
    const values = await rateForm.validateFields();

    const { group, room } = selectedRoomRate;

    const payload = {

      room_id: room.id,
      price_per_day: Number(values.price_per_day || 0),
      online_price: Number(values.online_price || 0),
      price_per_hour: Number(values.price_per_hour || 0),
      full_book: Number(values.full_book || 0),

    };

    const req = await GeneralService.updateRoomPrice(payload)

    if (req) {
      notify.success("Success", req.message)
      getRoombyType()
    } else {
      notify.error("Failed", "Something went wrong! Please try again!")
      getRoombyType()
    }

    console.log("UPDATE ROOM RATE PAYLOAD =>", payload);

    // Optional frontend local update
    setData((prev) =>
      prev.map((type) =>
        type.room_type_id === group.room_type_id
          ? {
            ...type,
            rooms: type.rooms.map((r) =>
              r.id === room.id
                ? {
                  ...r,
                  price_per_day: values.price_per_day,
                  online_price: values.online_price,
                  price_per_hour: values.price_per_hour,
                  full_book: values.full_book,
                }
                : r
            ),
          }
          : type
      )
    );

    setRateModalOpen(false);
    setSelectedRoomRate(null);
    rateForm.resetFields();
  };

  const filteredRoomTypes = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    if (!q) return data;

    return data
      .map((type) => {
        const typeMatched = String(type.room_type || "")
          .toLowerCase()
          .includes(q);

        const matchedRooms = (type.rooms || []).filter((room) =>
          String(room.room_number || "").toLowerCase().includes(q) ||
          String(room.room_name || "").toLowerCase().includes(q) ||
          String(room.id || "").includes(q)
        );

        return {
          ...type,
          rooms: typeMatched ? type.rooms : matchedRooms,
        };
      })
      .filter((type) => type.rooms.length > 0);
  }, [searchText, data]);

  const stopDrag = () => {
    if (!drag) return;
    const fromIndex = Math.min(drag.startIndex, drag.endIndex);
    const toIndex = Math.max(drag.startIndex, drag.endIndex);
    const range = { ...drag, fromIndex, toIndex, fromDate: dates[fromIndex], toDate: dates[toIndex] };

    // Single-cell click on an already-overridden cell → pre-fill with its current %
    if (fromIndex === toIndex) {
      const date = dates[fromIndex];
      let existingPercent = 0;
      if (drag.level === "type") {
        const key = getPriceKey({ level: "type", roomTypeId: drag.roomTypeId, priceKey: drag.priceKey, date });
        existingPercent = prices[key]?.percent ?? 0;
      } else {
        const roomKey = getPriceKey({ level: "room", id: drag.id, priceKey: drag.priceKey, date });
        if (prices[roomKey]) {
          existingPercent = prices[roomKey].percent ?? 0;
        } else {
          // Inherited from type — pre-fill with the type's %
          const group = data.find((g) => g.room_type_id === drag.roomTypeId);
          const typeKey = getPriceKey({ level: "type", roomTypeId: drag.roomTypeId, priceKey: drag.priceKey, date });
          existingPercent = prices[typeKey]?.percent ?? 0;
        }
      }
      setDrag(null);
      openModal(range, existingPercent);
      return;
    }

    setDrag(null);
    openModal(range, 0);
  };

  const isDraggingCell = ({ level, roomTypeId, id, priceKey, dateIndex }) => {
    if (!drag) return false;
    const start = Math.min(drag.startIndex, drag.endIndex);
    const end = Math.max(drag.startIndex, drag.endIndex);
    return drag.level === level && drag.roomTypeId === roomTypeId && drag.id === id && drag.priceKey === priceKey && dateIndex >= start && dateIndex <= end;
  };

  const clearOverride = () => {
    if (!selected) return;
    const logEntries = [];

    setPrices((prev) => {
      const updated = { ...prev };
      for (let i = selected.fromIndex; i <= selected.toIndex; i++) {
        const date = dates[i];
        const key = selected.level === "type"
          ? getPriceKey({ level: "type", roomTypeId: selected.roomTypeId, priceKey: selected.priceKey, date })
          : getPriceKey({ level: "room", id: selected.id, priceKey: selected.priceKey, date });
        const prevData = prev[key];
        if (prevData) {
          const group = data.find((g) => g.room_type_id === selected.roomTypeId);
          const room = selected.level === "room" ? group?.rooms.find((r) => r.id === selected.id) : null;
          logEntries.push({
            action: "CLEAR",
            level: selected.level,
            room_type_id: selected.roomTypeId,
            room_type: group?.room_type ?? null,
            room_id: selected.id ?? null,
            room_number: room?.room_number ?? null,
            price_field: selected.priceKey,
            date: dateKey(date),
            base_price: prevData.basePrice,
            prev_percent: prevData.percent,
            prev_price: prevData.finalPrice,
            new_percent: 0,
            new_price: prevData.basePrice,
          });
        }
        delete updated[key];
      }
      return updated;
    });

    setModalOpen(false);
    form.resetFields();
  };

  const savePercent = async () => {
    const values = await form.validateFields();

    const percent = Number(values.percent || 0);
    const logEntries = [];


    const apiPayload = {
      level: selected.level,
      room_type_id: selected.roomTypeId,
      room_id: selected.id || null,

      price_key: selected.priceKey,

      from_date: selected.fromDate.format("YYYY-MM-DD"),
      to_date: selected.toDate.format("YYYY-MM-DD"),

      percent,
      last_change: formatDate()
    };

    const req = await GeneralService.addSeasonalPrice(apiPayload)
    if (req.status) {
      notify.success("Success", req.message)
    } else {
      notify.error("Try Again!", req.message)
    }

    console.log("SAVE API PAYLOAD =>", apiPayload);


    setPrices((prev) => {
      const updated = { ...prev };
      for (let i = selected.fromIndex; i <= selected.toIndex; i++) {
        const date = dates[i];
        if (selected.level === "type") {
          const group = data.find((g) => g.room_type_id === selected.roomTypeId);
          const typeBasePrice = getTypeBasePrice({ group, priceKey: selected.priceKey });
          const finalPrice = calculatePercentPrice(typeBasePrice, percent);
          const typeKey = getPriceKey({ level: "type", roomTypeId: selected.roomTypeId, priceKey: selected.priceKey, date });
          const prevData = prev[typeKey];
          updated[typeKey] = { percent, basePrice: typeBasePrice, finalPrice, isEdited: true, fromType: true };
          logEntries.push({
            action: "SET",
            level: "type",
            room_type_id: selected.roomTypeId,
            room_type: group.room_type,
            room_id: null,
            room_number: null,
            price_field: selected.priceKey,
            date: dateKey(date),
            base_price: typeBasePrice,
            prev_percent: prevData?.percent ?? 0,
            prev_price: prevData?.finalPrice ?? typeBasePrice,
            new_percent: percent,
            new_price: finalPrice,
          });
        } else {
          const group = data.find((g) => g.room_type_id === selected.roomTypeId);
          const room = group.rooms.find((r) => r.id === selected.id);
          const basePrice = getRoomBasePrice({ room, priceKey: selected.priceKey });
          const finalPrice = calculatePercentPrice(basePrice, percent);
          const roomKey = getPriceKey({ level: "room", id: selected.id, priceKey: selected.priceKey, date });
          const prevData = prev[roomKey];
          updated[roomKey] = { percent, basePrice, finalPrice, isEdited: true, fromType: false };
          logEntries.push({
            action: "SET",
            level: "room",
            room_type_id: selected.roomTypeId,
            room_type: group.room_type,
            room_id: selected.id,
            room_number: room.room_number,
            price_field: selected.priceKey,
            date: dateKey(date),
            base_price: basePrice,
            prev_percent: prevData?.percent ?? 0,
            prev_price: prevData?.finalPrice ?? basePrice,
            new_percent: percent,
            new_price: finalPrice,
          });
        }
      }
      return updated;
    });

    // console.log("logEntries", logEntries);


    setModalOpen(false);
    form.resetFields();
  };

  /* ── Helpers to check if a single cell has its own override ── */
  const isCellOverridden = ({ level, roomTypeId, id, priceKey, date }) => {
    const key = level === "type"
      ? getPriceKey({ level: "type", roomTypeId, priceKey, date })
      : getPriceKey({ level: "room", id, priceKey, date });
    return !!prices[key];
  };

  const STICKY_HEADER_TOP = 76;
  /* ── Render ── */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .erp-root * { font-family: 'Inter', system-ui, sans-serif; box-sizing: border-box; }

        .erp-root .ant-btn {
          border-radius: 4px !important;
          border-color: ${T.border} !important;
          color: ${T.textSecondary} !important;
          font-size: 13px !important;
          height: 32px !important;
        }
        .erp-root .ant-btn:hover {
          border-color: ${T.accent} !important;
          color: ${T.accent} !important;
        }
        .erp-root .ant-select-selector {
          border-radius: 4px !important;
          border-color: ${T.border} !important;
          font-size: 13px !important;
          height: 32px !important;
        }
        .erp-root .ant-picker {
          border-radius: 4px !important;
          border-color: ${T.border} !important;
          font-size: 13px !important;
          height: 32px !important;
        }
        .erp-root .ant-modal-content { border-radius: 6px !important; }
        .erp-root .ant-modal-header { border-radius: 6px 6px 0 0 !important; padding: 16px 20px 12px !important; border-bottom: 1px solid ${T.border} !important; }
        .erp-root .ant-modal-title  { font-size: 15px !important; font-weight: 600 !important; color: ${T.textPrimary} !important; }
        .erp-root .ant-modal-footer { border-top: 1px solid ${T.border} !important; padding: 12px 20px !important; }
        .erp-root .ant-modal-footer .ant-btn-primary { background: ${T.accent} !important; border-color: ${T.accent} !important; color: #fff !important; border-radius: 4px !important; }

        .price-cell:hover { background: ${T.accentLight} !important; }
        .date-col-weekend { background: #F5F7FB !important; }

        .erp-scrollbar::-webkit-scrollbar        { width: 8px; height: 8px; }
        .erp-scrollbar::-webkit-scrollbar-track  { background: #F0F2F5; }
        .erp-scrollbar::-webkit-scrollbar-thumb  { background: #C5CAD6; border-radius: 4px; }
        .erp-scrollbar::-webkit-scrollbar-thumb:hover { background: #9AA2B3; }
      `}</style>

      <Card
        title={
          <TitleHead
            h1='Seasonal Princing Management'
            h2='Manage and override room rates across date ranges by type or individual unit.'
          />
        }
        extra={
          <Input.Search
            allowClear
            placeholder="Search room type, room no, room id"
            style={{ width: 400 }}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
          />
        }
      >



        {/* ── Main Card ── */}
        <div style={S.card}>

          {/* Toolbar */}
          <div style={S.toolbar}>
            <div style={S.toolbarLeft}>
              <CalendarOutlined style={{ color: T.accent, fontSize: 14 }} />
              <span style={S.dateRangeLabel}>{monthTitle}</span>
              <div style={S.divider} />
              <span style={S.recordCount}>
                {filteredRoomTypes.reduce((a, g) => a + g.rooms.length, 0)} rooms ·{" "}
                {filteredRoomTypes.length} types
              </span>
            </div>

            <Space size={6}>
              <Button icon={<LeftOutlined />} onClick={() => setCurrentDate(currentDate.subtract(1, "month"))} />
              <DatePicker
                picker="month"
                value={currentDate}
                onChange={(v) => v && setCurrentDate(v)}
                format="MMMM YYYY"
                style={{ width: 148 }}
              />
              <Button icon={<NextIcon />} onClick={() => setCurrentDate(currentDate.add(1, "month"))} />
              <div style={S.divider} />
              <Select
                value={daysCount}
                onChange={setDaysCount}
                style={{ width: 108 }}
                options={[
                  { label: "10 Days", value: 10 },
                  { label: "30 Days", value: 30 },
                  { label: "60 Days", value: 60 },
                ]}
              />
            </Space>
          </div>

          {/* Legend */}
          <div style={S.legend}>
            {[
              { color: T.editedBg, border: "#FCD34D", label: "Type override", dot: "#F59E0B" },
              { color: T.inheritBg, border: "#6EE7B7", label: "Inherited from type", dot: "#10B981" },
              { color: T.roomEditBg, border: "#93C5FD", label: "Room override", dot: "#3B82F6" },
              { color: T.dragBg, border: T.accent, label: "Selection", dot: null },
            ].map((l) => (
              <div key={l.label} style={S.legendItem}>
                <div style={{ position: "relative", width: 14, height: 14, flexShrink: 0 }}>
                  <div style={{ width: 14, height: 14, borderRadius: 2, background: l.color, border: `1px solid ${l.border}` }} />
                  {l.dot && <div style={{ position: "absolute", top: -2, right: -2, width: 5, height: 5, borderRadius: "50%", background: l.dot, border: "1px solid #fff" }} />}
                </div>
                <span style={S.legendText}>{l.label}</span>
              </div>
            ))}
            <div style={{ ...S.legendItem, marginLeft: 8 }}>
              <span style={{ ...S.legendText, color: T.textMuted, fontStyle: "italic" }}>
                Drag cells to set range · Click overridden/inherited cells to edit
              </span>
            </div>
          </div>

          {/* Grid */}
          <div
            className="erp-scrollbar"
            style={S.gridWrapper}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
          >
            <div style={{ ...S.grid, gridTemplateColumns: `300px repeat(${daysCount}, 100px)` }}>

              {/* Row 1: Month labels - sticky both vertically and horizontally via per-cell rendering */}
              <div style={{ ...S.cornerCell, top: 0, zIndex: 12 }} />
              {dates.map((date, index) => {
                // Show month label only on first date of each month group (or first date overall)
                const isFirst = index === 0 || date.format("YYYY-MM") !== dates[index - 1].format("YYYY-MM");
                return (
                  <div
                    key={`mhdr-${dateKey(date)}`}
                    style={{
                      ...S.monthHeader,
                      // Sticky horizontally: pin to left edge of the label column + first col width
                      ...(isFirst ? {
                        position: "sticky",
                        left: 300,
                        zIndex: 7,
                        borderLeft: `2px solid ${T.accentMid}`,
                        minWidth: 160,
                        background: T.surface,
                      } : {
                        background: T.surface,
                        color: "transparent", // hide repeated labels
                        borderLeft: "none",
                        zIndex: 5,
                      }),
                    }}
                  >
                    {isFirst ? date.format("MMMM YYYY") : ""}
                  </div>
                );
              })}

              {/* Row 2: Date headers */}
              <div style={{ ...S.cornerCell2, top: 38, zIndex: 11 }} />
              {dates.map((date) => {
                const isWknd = date.day() === 0 || date.day() === 6;
                return (
                  <div key={dateKey(date)} style={{ ...S.dateHeader, ...(isWknd ? S.weekendDate : {}) }}>
                    <span style={S.dateNum}>{date.format("D")}</span>
                    <span style={S.dateDow}>{date.format("ddd")}</span>
                  </div>
                );
              })}

              {/* Room type groups */}
              {filteredRoomTypes.map((group, gi) => (
                <React.Fragment key={group.room_type_id}>

                  {/* Group header row */}
                  <div
                    style={{
                      ...S.groupCell,
                      ...S.leftSticky,
                      position: "sticky",
                      top: STICKY_HEADER_TOP,
                      zIndex: 20,
                      background: T.surface,
                    }}
                    onClick={() =>
                      setExpanded((p) => ({
                        ...p,
                        [group.room_type_id]: !p[group.room_type_id],
                      }))
                    }
                  >
                    <div style={S.groupStripe} />
                    <span style={S.groupExpand}>
                      {expanded[group.room_type_id] ? <DownOutlined /> : <RightOutlined />}
                    </span>

                    <div>
                      <div style={S.groupName}>{group.room_type}</div>
                      <div style={S.groupSub}>{group.rooms.length} units</div>
                    </div>
                  </div>

                  {dates.map((date) => (
                    <div
                      key={dateKey(date)}
                      style={{
                        ...S.groupDateCell,
                        position: "sticky",
                        top: STICKY_HEADER_TOP,
                        zIndex: 8,
                        background: T.surface,
                      }}
                    >
                      <span style={S.availBadge}>
                        {group.rooms.length}/{group.rooms.length}
                      </span>
                    </div>
                  ))}
                  {/* Type-level price rows */}
                  {priceRows.map((row, ri) => (
                    <React.Fragment key={`type-${group.room_type_id}-${row.key}`}>
                      <div style={{ ...S.typePriceLabel, ...S.leftSticky }}>
                        <span style={S.priceRowBadge}>{row.short}</span>
                        <span style={S.priceRowFull}>{row.label}</span>
                      </div>
                      {dates.map((date, index) => {
                        const data = getTypePriceData({ group, priceKey: row.key, date });
                        const isDrag = isDraggingCell({ level: "type", roomTypeId: group.room_type_id, id: null, priceKey: row.key, dateIndex: index });
                        const isWknd = date.day() === 0 || date.day() === 6;
                        const overridden = isCellOverridden({ level: "type", roomTypeId: group.room_type_id, priceKey: row.key, date });
                        return (
                          <div
                            key={`type-${group.room_type_id}-${row.key}-${dateKey(date)}`}
                            className="price-cell"
                            style={{
                              ...S.priceCell,
                              background: isDrag ? T.dragBg : data.isEdited ? T.editedBg : isWknd ? "#FAFBFC" : T.surface,
                              borderLeft: isDrag ? `2px solid ${T.accent}` : overridden ? `2px solid #F59E0B` : "2px solid transparent",
                              cursor: overridden ? "pointer" : "crosshair",
                              position: "relative",
                            }}
                            onMouseDown={() => startDrag({ level: "type", roomTypeId: group.room_type_id, id: null, priceKey: row.key, priceLabel: row.label, dateIndex: index })}
                            onMouseEnter={() => moveDrag(index)}
                          >
                            {overridden && (
                              <span style={S.editDot} title="Click to edit override" />
                            )}
                            {/* <span style={S.priceVal}>{fmtPrice(data.finalPrice)}</span> */}
                            {data.percent > 0 && (
                              <span style={{ ...S.pctTag, background: "#FEF3C7", color: "#92400E", borderColor: "#FCD34D" }}>
                                +{data.percent}%
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}

                  {/* Expanded room rows */}
                  {expanded[group.room_type_id] && group.rooms.map((room) => (
                    <React.Fragment key={room.id}>
                      <div style={{ ...S.roomHeader, ...S.leftSticky }}>
                        <div style={S.roomDot} />
                        <div>
                          <div style={S.roomNo}>No. #{room.room_number} | Floor: {room.floor}</div>
                          <div style={S.id}>{room.room_name}</div>
                        </div>

                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openRoomRateModal(group, room);
                          }}
                          style={{
                            height: 24,
                            fontSize: 11,
                            padding: "0 8px",
                          }}
                        >
                          Edit Rate
                        </Button>
                      </div>
                      {dates.map((date) => (
                        <div key={`${room.id}-${dateKey(date)}`} style={S.blankCell} />
                      ))}
                      {priceRows.map((row) => (
                        <React.Fragment key={`${room.id}-${row.key}`}>
                          <div style={{ ...S.priceLabel, ...S.leftSticky }}>
                            <span style={S.priceRowBadge2}>{row.short}</span>
                            <span style={S.priceRowFull2}>{row.label}</span>
                          </div>
                          {dates.map((date, index) => {
                            const data = getRoomPriceData({ group, room, priceKey: row.key, date });
                            const isDrag = isDraggingCell({ level: "room", roomTypeId: group.room_type_id, id: room.id, priceKey: row.key, dateIndex: index });
                            const isWknd = date.day() === 0 || date.day() === 6;
                            const overridden = isCellOverridden({ level: "room", id: room.id, priceKey: row.key, date });
                            // Show edit indicator also on cells inherited from a type override
                            const hasEdit = overridden || data.fromType;
                            const cellBg = isDrag ? T.dragBg : data.fromType ? T.inheritBg : data.isEdited ? T.roomEditBg : isWknd ? "#FAFBFC" : T.surface;
                            const leftBorder = isDrag
                              ? `2px solid ${T.accent}`
                              : overridden
                                ? `2px solid #3B82F6`
                                : data.fromType
                                  ? `2px solid #10B981`
                                  : "2px solid transparent";
                            return (
                              <div
                                key={`${room.id}-${row.key}-${dateKey(date)}`}
                                className="price-cell"
                                style={{
                                  ...S.priceCell, background: cellBg,
                                  borderLeft: leftBorder,
                                  cursor: hasEdit ? "pointer" : "crosshair",
                                  position: "relative",
                                }}
                                onMouseDown={() => startDrag({ level: "room", roomTypeId: group.room_type_id, id: room.id, priceKey: row.key, priceLabel: row.label, dateIndex: index })}
                                onMouseEnter={() => moveDrag(index)}
                              >
                                {overridden && (
                                  <span style={{ ...S.editDot, background: "#3B82F6" }} title="Room override — click to edit" />
                                )}
                                {!overridden && data.fromType && (
                                  <span style={{ ...S.editDot, background: "#10B981" }} title="Inherited from type — click to override" />
                                )}
                                <span style={{ ...S.priceVal, fontSize: 11 }}>{fmtPrice(data.finalPrice)}</span>
                                {data.percent > 0 && (
                                  <span style={{
                                    ...S.pctTag,
                                    ...(data.fromType && !overridden
                                      ? { background: "#D1FAE5", color: "#065F46", borderColor: "#6EE7B7" }
                                      : { background: "#DBEAFE", color: "#1E40AF", borderColor: "#93C5FD" })
                                  }}>
                                    +{data.percent}%
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}

                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* ── Modal ── */}
        <Modal
          title={
            selected?.fromIndex === selected?.toIndex
              ? "Edit Price Override"
              : "Set Price Override"
          }
          open={modalOpen}
          onCancel={() => { setModalOpen(false); form.resetFields(); }}
          onOk={savePercent}
          okText="Apply Override"
          width={440}
          styles={{ body: { padding: "20px" } }}
          footer={(_, { OkBtn, CancelBtn }) => (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                {selected && isCellOverridden({
                  level: selected.level,
                  roomTypeId: selected.roomTypeId,
                  id: selected.id,
                  priceKey: selected.priceKey,
                  date: dates[selected.fromIndex],
                }) && (
                    <button
                      onClick={clearOverride}
                      style={{
                        background: "none", border: `1px solid #FCA5A5`, borderRadius: 4,
                        color: "#DC2626", fontSize: 12, fontWeight: 600, padding: "4px 12px",
                        cursor: "pointer", fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Clear Override
                    </button>
                  )}
              </div>
              <Space>
                <CancelBtn />
                <OkBtn />
              </Space>
            </div>
          )}
        >
          {selected && (
            <div style={S.modalMeta}>
              <div style={S.modalMetaRow}>
                <span style={S.modalMetaLabel}>Price Field</span>
                <span style={S.modalMetaVal}>{selected.priceLabel}</span>
              </div>

              <div style={S.modalMetaRow}>
                <span style={S.modalMetaLabel}>Date Range</span>
                <span style={S.modalMetaVal}>
                  {selected.fromDate.format("DD MMM YYYY")} – {selected.toDate.format("DD MMM YYYY")}
                </span>
              </div>
            </div>
          )}
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="percent"
              label={<span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>Price Hike (%)</span>}
              rules={[{ required: true, message: "Enter a percentage value" }]}
            >
              <InputNumber
                min={0}
                max={200}
                addonAfter="%"
                style={{ width: "100%" }}
                placeholder="e.g. 10"
              />
            </Form.Item>
            <p style={{ fontSize: 12, color: T.textMuted, marginTop: -8 }}>
              Final price = base price × (1 + hike%). Applies to all rooms in this range.
            </p>
          </Form>
        </Modal>

        <Modal
          title="Update Room Rates"
          open={rateModalOpen}
          onCancel={() => {
            setRateModalOpen(false);
            setSelectedRoomRate(null);
            rateForm.resetFields();
          }}
          onOk={updateRoomRates}
          okText="Update Rates"
          width={520}
        >
          {selectedRoomRate && (
            <div style={S.modalMeta}>
              <div style={S.modalMetaRow}>
                <span style={S.modalMetaLabel}>Room</span>
                <span style={S.modalMetaVal}>
                  {selectedRoomRate.room.room_number} - {selectedRoomRate.room.room_name}
                </span>
              </div>

              <div style={S.modalMetaRow}>
                <span style={S.modalMetaLabel}>Room Type</span>
                <span style={S.modalMetaVal}>{selectedRoomRate.group.room_type}</span>
              </div>
            </div>
          )}

          <Form form={rateForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="price_per_day"
              label="Room Price"
              rules={[{ required: true, message: "Enter room price" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} prefix="₹" />
            </Form.Item>

            <Form.Item
              name="online_price"
              label="Online Price"
              rules={[{ required: true, message: "Enter online price" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} prefix="₹" />
            </Form.Item>

            <Form.Item
              name="price_per_hour"
              label="Per Hour Price"
              rules={[{ required: true, message: "Enter per hour price" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} prefix="₹" />
            </Form.Item>

            {/* <Form.Item
              name="full_book"
              label="24 Hr Price"
              rules={[{ required: true, message: "Enter 24 hr price" }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} prefix="₹" />
            </Form.Item> */}
          </Form>
        </Modal>

      </Card>
    </>
  );
};



export default RoomByList;